import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../ui/card";
import { Link, useNavigate, useParams } from "react-router-dom";
import { dateFormatter } from "../../lib/formatters";
import React, { useContext, useEffect, useState } from "react";
import {
    useAuthInfo,
    WithLoggedInAuthInfoProps,
    withRequiredAuthInfo,
} from "@propelauth/react";
import { QAModel, QAReply } from "./QAModel";
import { Button } from "../ui/button";
import EditQA from "./EditQA";
import { UserContext } from "../../contexts/UserContext";
import { AutosizeTextarea } from "../ui/autoresizeTextArea";
import { createQAItem, getQaItem, deleteQaItem, getQaReplies } from "./qa_api";
import { toast } from "../ui/use-toast";
import EditReplyQA from "./EditReplyQA";
import { EditIcon, Trash2Icon } from "lucide-react";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";

type QAControls = {
    edit?: boolean;
    view?: boolean;
    canDelete?: boolean;
};

type QAReplyProps = {
    project_id: string;
    user_company: string;
    controls?: QAControls;
    size?: string,
    qa: QAReply;
    onEdit?: (qa: QAReply) => void;
};

type QACardProps = {
    project_id: string;
    user_company: string;
    controls?: QAControls;
    qa: QAModel;
    onEdit?: (qa: QAModel) => void;
};

type QAControlProps<T extends QAModel | QAReply> = {
    controls?: QAControls;
    qa: T;
    onEdit?: (qa: T) => void;
};

export function QAControls<T extends QAModel | QAReply>({
    controls,
    qa,
    onEdit,
}: QAControlProps<T>) {
    const { accessToken } = useAuthInfo();
    const navigate = useNavigate();
    const { project_id } = useParams<{ project_id: string }>();

    const handleDelete = () => {
        if (project_id && qa.id) {
            deleteQaItem(project_id, qa.id, accessToken!)
                .then(() => {
                    toast({
                        title: "Deleted",
                        description: "QA item successfully deleted.",
                    });
                    navigate(0);
                })
                .catch((error) => {
                    toast({
                        title: "Error",
                        description: error.message || "Failed to delete item.",
                        variant: "destructive",
                    });
                });
        }
    };

    const handleView = () => {
        if (project_id && qa.id) {
            navigate(`/project/${project_id}/qa/${qa.id}`);
        }
    };

    const handleEdit = () => {
        if (onEdit) {
            onEdit(qa);
        }
    };

    return (
        <div className="flex space-x-1">
            {controls?.view && (
                <Button onClick={handleView} variant="outline" size="sm">
                    View
                </Button>
            )}
            {controls?.edit && (
                <>
                    <Button onClick={handleEdit} variant="outline" size="sm">
                        <EditIcon size={12}/>
                    </Button>
                    <Button onClick={handleDelete} variant="outline" size="sm">
                        <Trash2Icon size={12}/>
                    </Button>
                </>
            )}
        </div>
    );
}

export function QACard({ project_id, qa, controls, onEdit }: QACardProps) {
    return (
        <Card key={qa.id} className="mb-4">
            <CardHeader>
                <CardTitle className="flex justify-between items-center">
                    <Link
                        className="text-lg font-semibold"
                        to={`/project/${project_id}/qa/${qa.id}`}
                    >
                        #{qa.number} - {qa.title}
                    </Link>
                    <QAControls<QAModel>
                        controls={controls}
                        qa={qa}
                        onEdit={onEdit}
                    />
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-base mb-2">{qa.body}</div>
                <div className="text-xs text-gray-500">
                    <span>{qa.created_by.name}, </span>
                    <span>{qa.created_by.company.name}, </span>
                    <span>{dateFormatter.format(new Date(qa.created_at))}</span>
                </div>
            </CardContent>
        </Card>
    );
}

export function QARespnseCard({
    project_id,
    qa,
    controls,
    onEdit,
    size,
}: QAReplyProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();
    const userContext = useContext(UserContext);
    const maxLength = 50

    const shouldShowExpandButton = qa.body.length > maxLength
    const isMyCompany = userContext?.company_id === qa.created_by.company.id
    // const isDeveloper = qa.created_by.company.company_type === 'developer';

    let cardBorder = '';
    let textColor = 'text-gray-800'; 

    switch (true) {
        case isMyCompany:
            cardBorder = 'border-gray-300 bg-gray-50'; 
            textColor = 'text-gray-700';
            break;
        // case isDeveloper:
        //     cardBorder = 'border-purple-300 bg-purple-50';
        //     textColor = 'text-purple-700';
        //     break;
        default:
            cardBorder = 'border-yellow-300 bg-yellow-50'; 
            textColor = 'text-yellow-700'; 
            break;
    }


    return (
        <Card
            key={qa.id}
            className={`mb-4 ml-6 border-l-4 ${cardBorder} flex items-center`}
        >
            <CardContent className="flex justify-between items-center w-full">
                <div>
                    <div className={`mb-2 pt-2 cursor-pointer ${textColor} ${size? size : ""}`}
                         onClick={()=> navigate(`/project/${project_id}/qa/${qa.id}`)}>
                    {isExpanded
          ? qa.body
          : `${qa.body.substring(0, maxLength)}${shouldShowExpandButton ? "..." : ""}`}
                    </div>
                    {shouldShowExpandButton && 
                    <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? "Show Less" : "Read More"}
                    </Button>}
                    <div className="text-xs text-gray-500">
                        <span className={textColor}>{qa.created_by.name}, </span>
                        <span className={textColor}>{qa.created_by.company.name}, </span>
                        <span className={textColor}>
                            {dateFormatter.format(new Date(qa.created_at))}
                        </span>
                        
                    </div>
                </div>
                <QAControls<QAReply>
                    controls={controls}
                    qa={qa}
                    onEdit={onEdit}
                />
            </CardContent>
        </Card>
    );
}

type ReplyThingProps = {
    saveReply: (a: string) => void;
    value: string;
    setValue: (a: string) => void;
};

export function ReplyThing({ saveReply, value, setValue }: ReplyThingProps) {
    return (
        <div className="flex flex-col space-y-2">
            <AutosizeTextarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Write your reply..."
                className="w-full p-2 border rounded"
            />
            <Button onClick={() => saveReply(value)} disabled={!value.trim()}>
                Reply
            </Button>
        </div>
    );
}

type ConversationProps = {
    parent_type?: string | null
    parent_id?: string | null
}


function QAThread({parent_type, parent_id}: ConversationProps) {
    const {accessToken} = useAuthInfo();
    const { project_id, qa_id } = useParams<{
        project_id: string;
        qa_id: string;
    }>();
    const thread_id = parent_id ? parent_id : qa_id;
    const [qaThread, setThread] = useState<QAModel | null>(null);
    const [replies, setReplies] = useState<QAReply[]>([]);
    const [skip, setSkip] = useState(0);
    const limit = 10;
    const [loadError, setLoadError] = useState<boolean>(false);
    const userContext = useContext(UserContext);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [currentReply, setCurrentReply] = useState<string>("");
    const [editingQA, setEditingQA] = useState<QAModel | QAReply | null>(null);

    useEffect(() => {
        getQaItem(project_id as string, thread_id as string, accessToken  as string)
            .then((data) => setThread(data))
            .catch((error) => {
                setLoadError(true);
                toast({
                    title: "Error",
                    description: error.message || "Failed to load.",
                    variant: "destructive",
                });
            });
    }, [project_id, thread_id, accessToken ]);

    useEffect(() => {
        loadReplies();
    }, [skip]);

    const loadReplies = () => {
        if (!project_id || !thread_id) return;

        getQaReplies(project_id, thread_id, accessToken as string, skip, limit)
            .then((newReplies) => {
                setReplies((prevReplies) => [...prevReplies, ...newReplies]);
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: error.message || "Failed to load replies.",
                    variant: "destructive",
                });
            });
    };

    const handleEditQAModel = (qaItem: QAModel) => {
        setEditingQA(qaItem);
    };

    const handleEditQAReply = (qaItem: QAReply) => {
        setEditingQA(qaItem);
    };

    const handleCancelEdit = () => {
        setEditingQA(null);
    };

    const handleSaveEdit = () => {
        getQaItem(project_id as string, thread_id as string, accessToken as string)
            .then((data) => {
                setThread(data);
                setEditingQA(null);
                setReplies(data.replies);
            })
            .catch((error) => {
                setLoadError(true);
                toast({
                    title: "Error",
                    description: error.message || "Failed to load.",
                    variant: "destructive",
                });
                setEditingQA(null);
            });
    };

    const onReply = (text: string) => {
        if (!text.trim()) {
            toast({
                title: "Error",
                description: "Reply cannot be empty.",
                variant: "destructive",
            });
            return;
        }

        if (!thread_id || !project_id || !qaThread) return;

        const body = {
            parent_id: thread_id,
            body: text,
        };

        setIsSubmitting(true);
        createQAItem(project_id, accessToken as string, body)
            .then((response) => {
                setReplies((prevReplies) => [...prevReplies, response]);
                setCurrentReply("");
                setIsSubmitting(false);
            })
            .catch((error) => {
                setIsSubmitting(false);
                toast({
                    title: "Error",
                    description: error.message || "Failed to create reply.",
                    variant: "destructive",
                });
            });
    };

    if (loadError) return <div>Error</div>;
    if (!qaThread) return <div><BaseLoadingSpinner/></div>;

    return (
        <div className="space-y-6">
            {editingQA && editingQA.id === qaThread.id ? (
                <EditQA
                    qa_id={qaThread.id}
                    onCancel={handleCancelEdit}
                    onSave={handleSaveEdit}
                />
            ) : (
                <QACard
                    qa={qaThread}
                    project_id={project_id as string}
                    controls={{
                        view: false,
                        edit: userContext?.email === qaThread.created_by.email,
                    }}
                    user_company={userContext?.company_id as string}
                    onEdit={handleEditQAModel}
                />
            )}

            {/*Area for the replies*/}
            <div className="ml-4">
                <h2 className="text-lg font-semibold mb-4">Replies</h2>
                {replies.length > 0 ? (
                    replies.map((qa_reply: QAReply) =>
                        editingQA && editingQA.id === qa_reply.id ? (
                            <EditReplyQA
                                key={qa_reply.id}
                                reply_id={qa_reply.id}
                                onCancel={handleCancelEdit}
                                onSave={handleSaveEdit}
                            />
                        ) : (
                            <QARespnseCard
                                key={qa_reply.id}
                                qa={qa_reply}
                                project_id={project_id as string}
                                controls={{
                                    view: false,
                                    edit:  userContext?.email === qa_reply.created_by.email,
                                }}
                                user_company={userContext?.company_id as string}
                                onEdit={handleEditQAReply}
                            />
                        )
                    )
                ) : (
                    <p className="text-gray-500">No replies yet.</p>
                )}
                <Button
                    onClick={() => setSkip((prevSkip) => prevSkip + limit)}
                    disabled={replies.length < skip + limit}
                    variant="outline"
                    className="mt-4"
                >
                    Load More
                </Button>
            </div>

            {/*Reply component */}
            <Card className="mt-6">
                <CardHeader>
                    <CardTitle>Post a Reply</CardTitle>
                </CardHeader>
                <CardContent>
                    <ReplyThing
                        saveReply={onReply}
                        value={currentReply}
                        setValue={setCurrentReply}
                    />
                </CardContent>
            </Card>
        </div>
    );
}

export default QAThread;
